import React from 'react';
import { Box, Chip, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography, FormHelperText } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';

const industryOptions = [
  { label: 'Construction', value: 'construction' },
  { label: 'Cleaning Services', value: 'cleaning' },
  { label: 'Labour Hire', value: 'labourhire' },
  { label: 'Landscaping', value: 'landscaping' },
  { label: 'Warehouse/Logistics', value: 'warehouse' },
  { label: 'Hospitality', value: 'hospitality' },
  { label: 'Manufacturing', value: 'manufacturing' },
  { label: 'Other', value: 'other' }
];

// hiring frequency removed per TODO

const communicationChannelOptions = [
  { label: 'Email', value: 'email' },
  { label: 'Phone Call', value: 'phone' },
  { label: 'SMS', value: 'sms' },
  { label: 'WhatsApp', value: 'whatsapp' },
];

const FieldSet = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: (theme) => theme.spacing(3),
      width: '100%',
      padding: (theme) => theme.spacing(3),
      borderRadius: (theme) => theme.shape.borderRadius,
      backgroundColor: (theme) => theme.palette.background.paper,
    }}
  >
    {children}
  </Box>
);

const validateABN = (abn) => {
  const cleanABN = abn.replace(/[^0-9]/g, '');
  if (cleanABN.length !== 11) return false;
  
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  let sum = 0;
  
  for (let i = 0; i < 11; i++) {
    let digit = parseInt(cleanABN[i], 10);
    if (i === 0)
      digit= digit - 1;
    sum += digit * weights[i];
  }
  return sum % 89 === 0;
};

const CompanyStep = () => {
  const { control, formState: { errors } } = useFormContext();
  const companyErrors = errors.company || {};

  return (
    <Stack spacing={3}>
      <Stack spacing={1} alignItems="center">
        <Typography variant="h4" color="text.primary">Company Profile</Typography>
        <Typography color="text.secondary">Tell us about your hiring needs</Typography>
      </Stack>

      <FieldSet>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <Controller
                name="company.companyName"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Enter your company name"
                    fullWidth
                    label="Company Name"
                    variant="outlined"
                    sx={{ bgcolor: 'background.input' }}
                    error={!!companyErrors.companyName}
                    helperText={companyErrors.companyName?.message}
                    required
                    data-testid="company-name"
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <Controller
                name="company.abn"
                control={control}
                defaultValue=""
                rules={{ validate: value => !value || validateABN(value) || "Please enter a valid ABN" }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    placeholder="Company ABN"
                    fullWidth
                    label="ABN"
                    variant="outlined"
                    sx={{ bgcolor: 'background.input' }}
                    error={!!error}
                    helperText={error ? error.message : ""}
                    data-testid="company-abn"
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth error={!!companyErrors.industry}>
              <InputLabel id="industry-label">Industry *</InputLabel>
              <Controller
                name="company.industry"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select sx={{ bgcolor: 'background.input' }} {...field} labelId="industry-label" label="Industry *" data-testid="company-industry">
                    {industryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {companyErrors.industry && (
                <FormHelperText>{companyErrors.industry.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Hiring frequency question removed */}

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <Controller
                name="company.companyPhone"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Company phone number"
                    fullWidth
                    label="Phone Number"
                    variant="outlined"
                    sx={{ bgcolor: 'background.input' }}
                    error={!!companyErrors.companyPhone}
                    helperText={companyErrors.companyPhone?.message}
                    required
                    data-testid="company-phone"
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <Controller
                name="company.companyEmail"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Company Email"
                    fullWidth
                    label="Email"
                    variant="outlined"
                    sx={{ bgcolor: 'background.input' }}
                    error={!!companyErrors.companyEmail}
                    helperText={companyErrors.companyEmail?.message}
                    required
                    data-testid="company-email"
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth error={!!companyErrors.preferredCommunication}>
              <InputLabel id="preferredCommunication-label">Preferred Communication Channels *</InputLabel>
              <Controller
                name="company.preferredCommunication"
                control={control}
                defaultValue={[]}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="preferredCommunication-label"
                    multiple
                    label="Preferred Communication Channels *"
                    sx={{ bgcolor: 'background.input' }}
                    data-testid="company-communication"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const option = communicationChannelOptions.find(opt => opt.value === value);
                          return (
                            <Chip key={value} label={option ? option.label : value} color="primary" variant="outlined" />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {communicationChannelOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormHelperText>
                {companyErrors.preferredCommunication ? companyErrors.preferredCommunication.message : 'Select all channels applicants can use to contact you'}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </FieldSet>
    </Stack>
  );
};

export default CompanyStep;